import { withOverlayControls } from '@eventbrite/eds-structure';
import { trackEventFromState } from '@eventbrite/site-analytics';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { usePrevious } from 'react-use';
import { LOCATION_DIALOG_CONTENT } from '../../../../constants';
import { FOLLOWED_ORGS_FILTER_VALUE } from '../../../../constants/filters';
import {
    getUserCurrentLocation as getUserCurrentLocationAction,
    searchLocationSuggestions as searchLocationSuggestionsAction,
} from '../../../../redux/actions/location';
import {
    handleApplyFilter as handleApplyFilterAction,
    handleRemoveFilter as handleRemoveFilterAction,
    handleRemoveOnlineFilter as handleRemoveOnlineFilterAction,
    searchAutocompleteSuggestions as searchAutocompleteSuggestionsAction,
    searchByBBoxOrPlace as searchByBBoxOrPlaceAction,
    searchEventSuggestions as searchEventSuggestionsAction,
} from '../../../../redux/actions/search';
import { openCloseAnimatedDrawer } from '../../../../redux/actions/ui';
import {
    getSearchHeaderTitle,
    getSelectedCategory,
    getSelectedDate,
    getSelectedFormat,
} from '../../../../redux/selectors/search';
import { DiscoverState, EventSearch } from '../../../../types';
import { SuggestionsState } from '../../../../types/index';
import { getEventSearchQueryKey } from '../../SearchPageData';
import { SearchInput } from './components';

const AdminSearchSubHeaderContainer = loadable(
    () => import('../AdminSearchSubHeader'),
    {
        resolveComponent: (components: {
            AdminSearchSubHeaderContainer: React.ReactElement;
        }) => components.AdminSearchSubHeaderContainer,
    },
);

interface SearchHeaderProps {
    /*
     * From withOverlayControls HoC
     */
    showOverlay: Function;
    hideOverlay?: Function;
    addOverlayNotification?: Function;
    hideOverlayNotification?: Function;
    trackGAEvent?: Function;
    handleApplyFilter?: Function;
    handleRemoveFilter?: Function;
    searchHeaderTitle?: string;
    selectedCategory?: string;
    selectedPrice?: string;
    selectedDate?: string;
    selectedFormat?: string;
    isBotRequest?: boolean;
    currentPlace?: string;
    term?: string;
    locationSlug?: string;
    locationDenied?: boolean;
    suggestions: SuggestionsState;
    handleUseCurrentLocation?: Function;
    handleSearchSuggestionChange?: Function;
    handleLocationSuggestionChange?: Function;
    handleLocationSelect?: Function;
    onGoogleMapsLoaded?: Function;
    isLoading?: boolean;
    facets?: {
        key?: string;
        count?: number;
        displayNam?: string;
    }[];
    /**
     * Callback to execute when a filter is selected
     */
    openCloseAnimatedDrawer?: Function;
    /**
     * Type of individual filter active in the filter list
     */
    filterTypeToShow?: $FixMe;
    selectedCurrency?: string;
    selectedLanguage?: string;
    followedOrganizers?: boolean;
    selectedOrganizations?: boolean;
    selectedHash?: string;
    isOnline?: boolean;
    handleApplyOnlineFilter?: Function;
    handleRemoveOnlineFilter?: Function;
    searchEventSuggestionsAction?: Function;
    isUsingCurrentLocation: boolean;
    history: RouteComponentProps['history'];
    location: {
        currentPlace?: string;
        currentPlaceParent?: string;
        placeType?: string;
    };
    eventSearch: EventSearch;
}

export const SearchHeader: React.FunctionComponent<SearchHeaderProps> = (
    props,
) => {
    const {
        searchHeaderTitle,
        isBotRequest,
        currentPlace,
        term,
        handleSearchSuggestionChange,
        handleLocationSuggestionChange,
        handleApplyFilter,
        suggestions,
        trackGAEvent,
        handleLocationSelect,
        location,
        locationDenied,
        history,
        handleUseCurrentLocation,
        searchEventSuggestionsAction,
        showOverlay,
        isUsingCurrentLocation,
        eventSearch,
    } = props;

    const prevLocationDenied = usePrevious(locationDenied);

    useEffect(() => {
        if (locationDenied && locationDenied !== prevLocationDenied) {
            showOverlay('dialog', LOCATION_DIALOG_CONTENT);
        }
    }, [locationDenied, showOverlay, prevLocationDenied]);

    const handleCurrentLocation = React.useCallback(() => {
        const shouldRedirect = false;
        handleUseCurrentLocation?.(shouldRedirect);
    }, [handleUseCurrentLocation]);
    const handleNewSearchQuery = useHandleNewSearchQuery({
        handleApplyFilter,
        eventSearch,
    });

    let headerContent = (
        <SearchInput
            searchEventSuggestionsAction={searchEventSuggestionsAction}
            hasHistory={true}
            handleSearchSuggestionChange={handleSearchSuggestionChange}
            handleLocationSuggestionChange={handleLocationSuggestionChange}
            handleUseCurrentLocation={handleCurrentLocation}
            handleApplyFilter={handleNewSearchQuery}
            initialSearchQuery={term}
            currentPlace={currentPlace}
            suggestions={suggestions}
            trackGAEvent={trackGAEvent}
            handleLocationSelect={handleLocationSelect}
            location={location}
            isUsingCurrentLocation={isUsingCurrentLocation}
            history={history}
        />
    );

    // for bots we will just show the title
    if (isBotRequest) {
        headerContent = (
            <h1 className="eds-text-hm eds-text-color--grey-700 search-header__bot-friendly">
                {searchHeaderTitle}
            </h1>
        );
    }

    return (
        <>
            <header className="search-header">
                {headerContent}
                {history?.location?.hash === '#debug' && (
                    <AdminSearchSubHeaderContainer />
                )}
            </header>
        </>
    );
};

function useHandleNewSearchQuery({
    eventSearch,
    handleApplyFilter,
}: Pick<SearchHeaderProps, 'handleApplyFilter' | 'eventSearch'>) {
    const queryClient = useQueryClient();
    return useCallback(
        (newEventSearch: EventSearch) => {
            // Force refresh if the user tries to search the same thing twice
            if (
                newEventSearch.q === eventSearch.q &&
                newEventSearch.page === eventSearch.page
            ) {
                queryClient.resetQueries({
                    queryKey: getEventSearchQueryKey(eventSearch),
                    exact: true,
                });
            } else {
                handleApplyFilter?.(newEventSearch);
            }
        },
        [eventSearch, queryClient, handleApplyFilter],
    );
}

const _mapStateToProps = (state: DiscoverState) => ({
    eventSearch: state.search.eventSearch,
    selectedPrice: state.search.eventSearch.price,
    selectedCategory: getSelectedCategory(state.search.eventSearch.tags),
    selectedFormat: getSelectedFormat(state.search.eventSearch.tags),
    selectedDate: getSelectedDate(
        state.search.eventSearch.dates,
        state.search.eventSearch.dateRange,
    ),
    selectedCurrency:
        state.search.eventSearch.currencies.length > 0
            ? state.search.eventSearch.currencies[0]
            : '',
    selectedLanguage:
        state.search.eventSearch.languages.length > 0
            ? state.search.eventSearch.languages
            : '',
    followedOrganizers: state.search.eventSearch?.special?.includes(
        FOLLOWED_ORGS_FILTER_VALUE,
    ),
    selectedOrganizations: state.search.eventSearch.organizationsOr?.length
        ? state.search.eventSearch.organizationsOr
        : false,
    selectedHash: state.search.eventSearch.hash,
    searchHeaderTitle: getSearchHeaderTitle(state),
    isBotRequest: state.app.isBotRequest,
    locationSlug: state.location?.slug,
    currentPlace: state.location?.currentPlace,
    isOnline: state.search.eventSearch.onlineEventsOnly,
    term: state.search.eventSearch.q,
    locationDenied: state.location?.locationDenied,
    currentLocationCoordinates: state.location?.currentLocationCoordinates,
    isUsingCurrentLocation: state.location.isUsingCurrentLocation,
    suggestions: state.suggestions,
    trackGAEvent: (action: string) => trackEventFromState(state, action),
    location: state.location,
    filterTypeToShow: state.ui.filterTypeToShow,
});

const _mapDispatchToProps = (
    dispatch: Function,
    ownProps: { history?: object },
) => ({
    handleApplyFilter: (...args: [$FixMe]) =>
        dispatch(handleApplyFilterAction(ownProps.history || {}, ...args)),
    handleRemoveFilter: (...args: [$FixMe]) =>
        dispatch(handleRemoveFilterAction(ownProps.history || {}, ...args)),
    handleUseCurrentLocation: (...args: [$FixMe]) =>
        dispatch(getUserCurrentLocationAction(ownProps.history || {}, ...args)),
    handleLocationSuggestionChange: (...args: [$FixMe]) =>
        dispatch(searchLocationSuggestionsAction(...args)),
    handleSearchSuggestionChange: (...args: any) =>
        dispatch(searchAutocompleteSuggestionsAction(...args)),
    handleLocationSelect: (...args: [$FixMe]) =>
        dispatch(searchByBBoxOrPlaceAction(ownProps.history || {}, ...args)),
    openCloseAnimatedDrawer: (...args: [$FixMe]) =>
        dispatch(openCloseAnimatedDrawer(...args)),
    handleApplyOnlineFilter: (...args: [$FixMe]) =>
        dispatch(searchByBBoxOrPlaceAction(ownProps.history || {}, ...args)),
    handleRemoveOnlineFilter: (...args: [$FixMe]) =>
        dispatch(
            handleRemoveOnlineFilterAction(ownProps.history || {}, ...args),
        ),
    searchEventSuggestionsAction: (...args: $FixMe) =>
        dispatch(searchEventSuggestionsAction(...args)),
});

export const SearchHeaderContainer = withRouter(
    withOverlayControls<any>(
        connect(_mapStateToProps, _mapDispatchToProps)(SearchHeader),
    ),
);
